import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Ticket, DollarSign, Users, TrendingUp, 
  ArrowRight, RefreshCw, Eye, QrCode, 
  CalendarDays, ChevronRight, AlertCircle, Wallet,
  ShoppingCart, UserPlus, BarChart2, Settings, Megaphone,
  Clock, CheckCircle, XCircle, ArrowUpRight, ArrowDownRight,
  Banknote, FileText, Bell, Download
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { statsApi, eventsApi, calendarApi, analyticsApi } from '../../lib/api';
import api from '../../lib/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } }
};

// Metric Card - Futuristic with glow effects
const MetricCard = ({ icon: Icon, label, value, trend, trendLabel, color = "gold", delta = null }) => {
  const colors = {
    gold: { 
      bg: "bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent", 
      border: "border-amber-500/30", 
      text: "text-amber-400", 
      icon: "text-amber-500",
      glow: "shadow-amber-500/20"
    },
    green: { 
      bg: "bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent", 
      border: "border-emerald-500/30", 
      text: "text-emerald-400", 
      icon: "text-emerald-500",
      glow: "shadow-emerald-500/20"
    },
    blue: { 
      bg: "bg-gradient-to-br from-blue-500/15 via-blue-500/5 to-transparent", 
      border: "border-blue-500/30", 
      text: "text-blue-400", 
      icon: "text-blue-500",
      glow: "shadow-blue-500/20"
    },
    purple: { 
      bg: "bg-gradient-to-br from-purple-500/15 via-purple-500/5 to-transparent", 
      border: "border-purple-500/30", 
      text: "text-purple-400", 
      icon: "text-purple-500",
      glow: "shadow-purple-500/20"
    },
    red: { 
      bg: "bg-gradient-to-br from-red-500/15 via-red-500/5 to-transparent", 
      border: "border-red-500/30", 
      text: "text-red-400", 
      icon: "text-red-500",
      glow: "shadow-red-500/20"
    }
  };
  const c = colors[color];

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.02, y: -2 }}
      className={`relative ${c.bg} ${c.border} border rounded-2xl p-4 hover:shadow-lg ${c.glow} transition-all duration-300 backdrop-blur-sm overflow-hidden group`}
    >
      {/* Glow effect */}
      <div className={`absolute -top-12 -right-12 w-24 h-24 rounded-full ${c.bg} blur-2xl opacity-50 group-hover:opacity-80 transition-opacity`} />
      
      <div className="relative flex items-start justify-between">
        <div className="flex-1">
          <div className={`inline-flex p-2 rounded-xl ${c.bg} ${c.border} border mb-3`}>
            <Icon size={18} className={c.icon} />
          </div>
          <p className={`text-2xl font-black ${c.text} tracking-tight`}>{value}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{label}</p>
          {delta && (
            <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
              delta.direction === 'up' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' :
              delta.direction === 'down' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
              'bg-zinc-700/20 text-zinc-400 border border-zinc-700/30'
            }`} title={delta.tooltip}>
              <span>{delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : '–'}</span>
              <span>{delta.label}</span>
            </div>
          )}
        </div>
        {trendLabel && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.bg} ${c.text} border ${c.border}`}>
            {trendLabel}
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Quick Action Button - Futuristic
const QuickAction = ({ icon: Icon, label, href, color, badge }) => (
  <Link to={href}>
    <motion.div 
      variants={itemVariants}
      whileHover={{ scale: 1.05, y: -3 }}
      whileTap={{ scale: 0.98 }}
      className="relative bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-950 border border-zinc-800 rounded-2xl p-4 hover:border-zinc-600 transition-all group overflow-hidden backdrop-blur-sm"
    >
      {/* Background glow on hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl"
        style={{ backgroundColor: color }}
      />
      
      {badge && (
        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/50">
          {badge}
        </span>
      )}
      <div 
        className="relative w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110"
        style={{ 
          backgroundColor: `${color}20`,
          boxShadow: `0 0 20px ${color}30`
        }}
      >
        <Icon size={20} style={{ color }} className="transition-transform group-hover:scale-110" />
      </div>
      <p className="relative text-sm font-semibold text-zinc-400 group-hover:text-white transition-colors">{label}</p>
      <div className="absolute bottom-3 right-3 w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center group-hover:bg-zinc-700 transition-colors">
        <ChevronRight size={12} className="text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
      </div>
    </motion.div>
  </Link>
);

// Alert Item
const AlertItem = ({ type, title, description, action, href, time }) => {
  const typeStyles = {
    warning: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
    success: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
    error: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
    info: { icon: Bell, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" }
  };
  const style = typeStyles[type] || typeStyles.info;
  const IconComp = style.icon;

  return (
    <Link to={href || "#"} className="block">
      <div className={`${style.bg} ${style.border} border rounded-lg p-3 hover:scale-[1.01] transition-transform`}>
        <div className="flex gap-3">
          <IconComp size={18} className={`${style.color} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{title}</p>
            <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{description}</p>
            {time && <p className="text-xs text-zinc-600 mt-1">{time}</p>}
          </div>
          {action && (
            <button className="text-xs text-amber-400 hover:text-amber-300 font-medium flex-shrink-0">
              {action}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [period, setPeriod] = useState('current_month');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pendingSettlements, setPendingSettlements] = useState([]);
  const [pendingProducers, setPendingProducers] = useState(0);
  const [pendingEvents, setPendingEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    try {
      const [statsRes, calendarRes, analyticsRes, monthlyRes] = await Promise.all([
        api.get(`/stats?period=${period}`),
        calendarApi.getEvents(),
        analyticsApi.getSummary(7),
        api.get('/stats/monthly-history?months=6').catch(() => ({ data: { series: [] } })),
      ]);
      setStats(statsRes.data);
      // Filter upcoming events: keep events whose effective end (end OR start+6h) is in the future
      const now = Date.now();
      const upcoming = (calendarRes.data || []).filter(ev => {
        const startMs = ev.start ? new Date(ev.start).getTime() : 0;
        const endMs = ev.end ? new Date(ev.end).getTime() : (startMs ? startMs + 6 * 60 * 60 * 1000 : 0);
        return endMs >= now;
      });
      // Sort by start ascending
      upcoming.sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
      setCalendarEvents(upcoming.slice(0, 5));
      setAnalytics(analyticsRes.data);
      setMonthlyHistory(monthlyRes.data?.series || []);
      
      // Fetch pending settlements (real Liquidaciones data)
      try {
        const settlementsRes = await api.get('/settlements/admin/all?status=pending');
        const list = settlementsRes.data?.settlements || settlementsRes.data || [];
        setPendingSettlements(Array.isArray(list) ? list : []);
      } catch (e) { console.log('Settlements not available'); }
      
      // Fetch pending counts
      try {
        const eventsRes = await eventsApi.getAll();
        const pending = eventsRes.data?.filter(e => e.status === 'pending') || [];
        setPendingEvents(pending.length);
      } catch (e) { console.log('Events count not available'); }

      try {
        const producersRes = await api.get('/producers/admin/applications?status=pending');
        setPendingProducers(producersRes.data?.total || producersRes.data?.applications?.length || 0);
      } catch (e) { console.log('Producers count not available'); }
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Month-over-month delta helper -----------------------------------
  // Picks the current period vs the previous one in the monthly-history series
  // (or vs `last_month` when in `current_month`). Returns null if not enough data.
  const getDelta = (key) => {
    if (!monthlyHistory || monthlyHistory.length < 2) return null;
    if (period === 'all') return null;
    let idx;
    if (period === 'current_month') {
      idx = monthlyHistory.length - 1; // last item is current month
    } else if (period === 'last_month') {
      idx = monthlyHistory.length - 2;
    } else if (period.startsWith('month=')) {
      const m = period.split('=')[1];
      idx = monthlyHistory.findIndex(x => x.month === m);
    } else {
      idx = -1;
    }
    if (idx <= 0) return null;
    const cur = monthlyHistory[idx]?.[key] ?? 0;
    const prev = monthlyHistory[idx - 1]?.[key] ?? 0;
    if (!prev && !cur) return null;
    if (!prev) return { direction: 'up', label: 'Nuevo', tooltip: `Mes anterior: $0` };
    const pct = ((cur - prev) / prev) * 100;
    const direction = pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat';
    const abs = Math.abs(pct);
    const label = abs >= 1000 ? `${(abs / 1000).toFixed(1)}k%` : `${abs.toFixed(0)}% vs mes pasado`;
    return {
      direction,
      label: direction === 'flat' ? '0% vs mes pasado' : label,
      tooltip: `Anterior: $${Number(prev).toLocaleString()} → Actual: $${Number(cur).toLocaleString()}`,
    };
  };

  // ---- Export monthly history as CSV ------------------------------------
  const exportMonthlyCsv = () => {
    if (!monthlyHistory || monthlyHistory.length === 0) return;
    const rows = [
      ['Mes', 'Etiqueta', 'Ingresos Brutos', 'Ganancia Neta (Plataforma)', 'Para Organizadores', 'Cantidad de Órdenes'],
      ...monthlyHistory.map(m => [
        m.month,
        m.label,
        (m.total_revenue ?? 0).toFixed(2),
        (m.service_fee_total ?? 0).toFixed(2),
        (m.net_revenue ?? 0).toFixed(2),
        m.orders ?? 0,
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM so Excel reads ñ/á right
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bravo-finanzas-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Quick Actions - Updated
  const quickActions = [
    { icon: CalendarDays, label: 'Nuevo Evento', href: '/admin/eventos', color: '#D4AF37' },
    { icon: QrCode, label: 'Scanner', href: '/admin/tickets', color: '#10B981' },
    { icon: Megaphone, label: 'Marketing', href: '/admin/marketing', color: '#8B5CF6' },
    { icon: Wallet, label: 'Liquidaciones', href: '/admin/liquidaciones', color: '#F59E0B', badge: pendingSettlements.length || null },
    { icon: Users, label: 'Productoras', href: '/admin/productoras', color: '#3B82F6', badge: pendingProducers || null },
    { icon: ShoppingCart, label: 'Ver Órdenes', href: '/admin/eventos', color: '#EC4899' },
    { icon: UserPlus, label: 'Servicios', href: '/admin/prensa', color: '#06B6D4' },
    { icon: Settings, label: 'Configuración', href: '/admin/configuracion', color: '#71717A' },
  ];

  // Generate alerts based on data
  const alerts = [];
  if (pendingSettlements.length > 0) {
    alerts.push({
      type: 'warning',
      title: `${pendingSettlements.length} liquidación${pendingSettlements.length > 1 ? 'es' : ''} pendiente${pendingSettlements.length > 1 ? 's' : ''}`,
      description: 'Hay solicitudes de liquidación esperando aprobación',
      href: '/admin/liquidaciones',
      action: 'Ver'
    });
  }
  if (pendingEvents > 0) {
    alerts.push({
      type: 'info',
      title: `${pendingEvents} evento${pendingEvents > 1 ? 's' : ''} por aprobar`,
      description: 'Eventos de productoras esperando revisión',
      href: '/admin/eventos',
      action: 'Revisar'
    });
  }
  if (pendingProducers > 0) {
    alerts.push({
      type: 'info',
      title: `${pendingProducers} productora${pendingProducers > 1 ? 's' : ''} pendiente${pendingProducers > 1 ? 's' : ''}`,
      description: 'Nuevas solicitudes de registro de productoras',
      href: '/admin/productoras',
      action: 'Ver'
    });
  }

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="p-6 lg:p-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 bg-zinc-900 rounded-xl animate-pulse" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 h-80 bg-zinc-900 rounded-xl animate-pulse" />
              <div className="h-80 bg-zinc-900 rounded-xl animate-pulse" />
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const maxDailyViews = Math.max(...(analytics?.daily_views?.map(d => d.views) || [1]));

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="p-6 lg:p-8 space-y-8"
          data-testid="admin-dashboard"
        >
          {/* Header - Futuristic */}
          <motion.div variants={itemVariants} className="relative">
            {/* Background glow */}
            <div className="absolute -top-20 -left-20 w-60 h-60 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="absolute -top-10 right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl" />
            
            <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-1 h-8 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full" />
                  <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
                    Dashboard
                  </h1>
                </div>
                <p className="text-zinc-500 text-sm ml-4">
                  Bienvenido de vuelta, <span className="text-amber-400 font-medium">{user?.full_name || user?.username || 'Admin'}</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/50">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-zinc-400">
                    {format(lastUpdate, "HH:mm 'hs'", { locale: es })}
                  </span>
                </div>
                <Link to="/admin/campanas/nueva" data-testid="dashboard-create-campaign-btn">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black font-bold text-sm shadow-lg shadow-amber-500/20"
                  >
                    <Megaphone size={15} />
                    <span className="hidden sm:inline">Crear Campaña</span>
                  </motion.button>
                </Link>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fetchData(true)}
                  disabled={isRefreshing}
                  className="p-3 rounded-xl bg-gradient-to-br from-zinc-800 to-zinc-900 hover:from-zinc-700 hover:to-zinc-800 border border-zinc-700 transition-all shadow-lg"
                  data-testid="refresh-dashboard"
                >
                  <RefreshCw size={16} className={`text-zinc-300 ${isRefreshing ? 'animate-spin' : ''}`} />
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Main Metrics - Reorganized with better spacing */}
          <div className="space-y-6">
            {/* Row 1: Key Performance */}
            <motion.div variants={containerVariants}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-5 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-full" />
                <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider">
                  Métricas Principales
                </h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  icon={Eye}
                  label="Visitas Hoy"
                  value={analytics?.today_views?.toLocaleString() || '0'}
                  trendLabel={`${analytics?.today_unique_visitors || 0} únicos`}
                  color="gold"
                />
                <MetricCard
                  icon={Users}
                  label="Usuarios"
                  value={stats?.total_public_users?.toLocaleString() || '0'}
                  trendLabel={`${stats?.active_subscribers || 0} BRAVX activos`}
                  color="blue"
                />
                <MetricCard
                  icon={Ticket}
                  label="Tickets Vendidos"
                  value={stats?.total_tickets?.toLocaleString() || '0'}
                  color="green"
                />
                <MetricCard
                  icon={Calendar}
                  label="Eventos Publicados"
                  value={stats?.published_events?.toLocaleString() || '0'}
                  color="purple"
                />
              </div>
            </motion.div>

            {/* Row 2: Financial Metrics */}
            <motion.div variants={containerVariants}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-5 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full" />
                  <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider">
                    Finanzas
                  </h2>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                    {period === 'current_month' ? 'Mes actual' : period === 'last_month' ? 'Mes pasado' : period === 'all' ? 'Histórico' : (stats?.period || period)}
                  </span>
                </div>
                {/* Period selector */}
                <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1" data-testid="finance-period-picker">
                  {[
                    { id: 'current_month', label: 'Mes actual' },
                    { id: 'last_month',    label: 'Mes pasado' },
                    { id: 'all',           label: 'Histórico' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setPeriod(opt.id)}
                      data-testid={`period-${opt.id}`}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${period === opt.id ? 'bg-amber-500/20 text-amber-300' : 'text-zinc-400 hover:text-white'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {/* History months dropdown */}
                  {monthlyHistory.length > 0 && (
                    <select
                      value={period.startsWith('month=') ? period : ''}
                      onChange={(e) => e.target.value && setPeriod(e.target.value)}
                      className="bg-transparent text-zinc-400 text-xs ml-1 outline-none cursor-pointer"
                      data-testid="period-history-select"
                    >
                      <option value="">Histórico ▼</option>
                      {monthlyHistory.slice().reverse().map(m => (
                        <option key={m.month} value={`month=${m.month}`}>{m.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                  icon={DollarSign}
                  label="Ingresos Brutos"
                  value={typeof stats?.total_revenue === 'number' ? `$${stats.total_revenue.toLocaleString()}` : '---'}
                  trendLabel={`${stats?.total_orders || 0} órdenes`}
                  color="blue"
                  delta={getDelta('total_revenue')}
                />
                <MetricCard
                  icon={TrendingUp}
                  label="Ganancia Neta"
                  value={typeof stats?.service_fee_total === 'number' ? `$${stats.service_fee_total.toLocaleString()}` : '---'}
                  trendLabel="Cargo por servicio"
                  color="gold"
                  delta={getDelta('service_fee_total')}
                />
                <MetricCard
                  icon={Wallet}
                  label="Para Organizadores"
                  value={typeof stats?.net_revenue === 'number' ? `$${stats.net_revenue.toLocaleString()}` : '---'}
                  trendLabel="Bruto - cargo servicio"
                  color="green"
                  delta={getDelta('net_revenue')}
                />
              </div>

              {/* Monthly history mini-chart */}
              {monthlyHistory.length > 0 && (
                <div className="mt-4 bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="monthly-history-chart">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart2 size={14} className="text-amber-400" />
                      <p className="text-xs text-zinc-300 font-semibold uppercase tracking-wider">Últimos {monthlyHistory.length} meses</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={exportMonthlyCsv}
                        className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-amber-300 px-2 py-1 rounded-md border border-zinc-700 hover:border-amber-500/50 transition-colors"
                        data-testid="finance-csv-download-btn"
                        title="Descargar historial mensual en formato CSV (Excel)"
                      >
                        <Download size={11} /> CSV
                      </button>
                      <p className="text-[10px] text-zinc-500">Brutos vs ganancia neta</p>
                    </div>
                  </div>
                  <div className="flex items-end justify-between gap-2 h-32">
                    {monthlyHistory.map((m, i) => {
                      const max = Math.max(1, ...monthlyHistory.map(x => x.total_revenue));
                      const grossH = (m.total_revenue / max) * 100;
                      const feeH = (m.service_fee_total / max) * 100;
                      const isPicked = period === `month=${m.month}` || (period === 'current_month' && m.is_current) || (period === 'last_month' && i === monthlyHistory.length - 2);
                      return (
                        <button
                          key={m.month}
                          type="button"
                          onClick={() => setPeriod(`month=${m.month}`)}
                          className="flex-1 flex flex-col items-center gap-1.5 group cursor-pointer"
                          title={`${m.label}: $${m.total_revenue.toLocaleString()} bruto · $${m.service_fee_total.toLocaleString()} neto plataforma`}
                          data-testid={`history-bar-${m.month}`}
                        >
                          <span className={`text-[9px] font-mono ${isPicked ? 'text-amber-300' : 'text-zinc-500'} group-hover:text-amber-300`}>
                            ${m.total_revenue >= 1000 ? (m.total_revenue / 1000).toFixed(1) + 'k' : m.total_revenue.toFixed(0)}
                          </span>
                          <div className="w-full bg-zinc-800/60 rounded-sm h-24 flex items-end overflow-hidden relative">
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: `${grossH}%` }}
                              transition={{ duration: 0.5, delay: i * 0.05 }}
                              className={`w-full rounded-sm ${isPicked ? 'bg-gradient-to-t from-amber-500 to-amber-300' : 'bg-gradient-to-t from-blue-500/50 to-blue-400/30'} group-hover:from-amber-500 group-hover:to-amber-300`}
                            />
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: `${feeH}%` }}
                              transition={{ duration: 0.5, delay: i * 0.05 + 0.1 }}
                              className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-500/70 to-emerald-300/50"
                            />
                          </div>
                          <span className={`text-[9px] uppercase ${isPicked ? 'text-amber-300 font-bold' : 'text-zinc-500'}`}>{m.label.split(' ')[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <motion.div variants={itemVariants}>
              <div className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-transparent border border-zinc-800/50 rounded-2xl p-5 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 left-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl" />
                <h2 className="relative text-sm font-bold text-zinc-300 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20">
                    <Bell size={14} className="text-red-400" />
                  </div>
                  Alertas y Pendientes
                </h2>
                <div className="relative space-y-2">
                  {alerts.map((alert, i) => (
                    <AlertItem key={i} {...alert} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Main Grid - Traffic & Events */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Traffic Chart */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-transparent border border-zinc-800/50 rounded-2xl p-5 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl" />
                <div className="relative flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <BarChart2 size={18} className="text-amber-400" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">Tráfico Web</h2>
                      <p className="text-xs text-zinc-500">Últimos 7 días</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-amber-400">{analytics?.total_views?.toLocaleString() || 0}</p>
                    <p className="text-xs text-zinc-500">visitas totales</p>
                  </div>
                </div>
                
                {/* Chart */}
                <div className="flex items-end justify-between gap-2 h-36">
                  {analytics?.daily_views?.slice(-7).map((day, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      <span className="text-[10px] text-zinc-500 font-medium">{day.views}</span>
                      <div className="w-full bg-zinc-800 rounded-sm h-28 flex items-end overflow-hidden">
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${(day.views / maxDailyViews) * 100}%` }}
                          transition={{ duration: 0.4, delay: i * 0.05 }}
                          className="w-full bg-gradient-to-t from-amber-500 to-amber-400/60 rounded-sm"
                        />
                      </div>
                      <span className="text-[10px] text-zinc-600">{day.date?.slice(-2) || i}</span>
                    </div>
                  ))}
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 pt-4 mt-4 border-t border-zinc-800">
                  <div>
                    <p className="text-xs text-zinc-500">Promedio diario</p>
                    <p className="font-semibold text-amber-400">{analytics?.avg_views_per_day || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Visitantes únicos</p>
                    <p className="font-semibold text-blue-400">{analytics?.unique_visitors || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Período</p>
                    <p className="font-semibold text-zinc-300">{analytics?.period_days || 7} días</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Pending Settlements */}
            <motion.div variants={itemVariants}>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 h-full">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-semibold text-white flex items-center gap-2">
                      <Banknote size={18} className="text-amber-400" />
                      Liquidaciones
                    </h2>
                    {(stats?.pending_settlements_count || 0) > 0 && typeof stats?.pending_settlements_amount === 'number' && (
                      <p className="text-[10px] text-zinc-500 mt-0.5">
                        {stats.pending_settlements_count} pendiente(s) · <span className="text-amber-400 font-mono">${stats.pending_settlements_amount.toLocaleString()}</span>
                      </p>
                    )}
                  </div>
                  <Link 
                    to="/admin/liquidaciones"
                    className="text-xs text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1"
                  >
                    Ver todas
                    <ChevronRight size={12} />
                  </Link>
                </div>
                
                {pendingSettlements.length > 0 ? (
                  <div className="space-y-3">
                    {pendingSettlements.slice(0, 3).map((settlement, i) => (
                      <Link
                        to="/admin/liquidaciones"
                        key={settlement.id || i}
                        className="block bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50 hover:border-amber-500/40 transition-colors"
                        data-testid={`settlement-card-${settlement.id || i}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {settlement.entity_name || settlement.producer_name || 'Sin nombre'}
                            </p>
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {settlement.entity_type === 'producer' ? 'Productora' : settlement.entity_type === 'dj' ? 'DJ' : settlement.entity_type === 'service_provider' ? 'Servicio' : (settlement.entity_type || 'Liquidación')}
                              {settlement.requested_at && ` · ${new Date(settlement.requested_at).toLocaleDateString('es-AR')}`}
                            </p>
                          </div>
                          <p className="text-amber-400 font-semibold whitespace-nowrap ml-2">
                            ${Number(settlement.amount || 0).toLocaleString()}
                          </p>
                        </div>
                      </Link>
                    ))}
                    {pendingSettlements.length > 3 && (
                      <Link to="/admin/liquidaciones" className="block text-center text-xs text-amber-400 hover:text-amber-300 pt-1">
                        Ver {pendingSettlements.length - 3} más →
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <CheckCircle size={32} className="text-emerald-500/50 mb-2" />
                    <p className="text-sm text-zinc-500">Sin liquidaciones pendientes</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Quick Actions - Futuristic Grid */}
          <motion.div variants={itemVariants}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
              <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider">
                Accesos Rápidos
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {quickActions.map((action, i) => (
                <QuickAction key={i} {...action} />
              ))}
            </div>
          </motion.div>

          {/* Bottom Grid - Glassmorphism */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            {analytics?.top_pages?.length > 0 && (
              <motion.div variants={itemVariants}>
                <div className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-transparent border border-zinc-800/50 rounded-2xl p-5 backdrop-blur-sm overflow-hidden">
                  {/* Background accent */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
                  
                  <h2 className="relative font-semibold text-white flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <FileText size={16} className="text-amber-400" />
                    </div>
                    Páginas Populares
                  </h2>
                  <div className="relative space-y-3">
                    {analytics.top_pages.slice(0, 5).map((page, index) => {
                      const maxViews = analytics.top_pages[0]?.views || 1;
                      const percentage = (page.views / maxViews) * 100;
                      const pageName = page.page === '/' ? 'Inicio' : 
                        page.page.replace(/^\//, '').replace(/-/g, ' ').split('/')[0];
                      
                      return (
                        <div key={index}>
                          <div className="flex justify-between items-center mb-1.5">
                            <span className="text-sm text-zinc-300 capitalize truncate">
                              {pageName}
                            </span>
                            <span className="text-xs font-mono text-amber-400">{page.views}</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ delay: index * 0.08, duration: 0.4 }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Upcoming Events */}
            <motion.div variants={itemVariants}>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <CalendarDays size={18} className="text-amber-400" />
                    Próximos Eventos
                  </h2>
                  <Link 
                    to="/admin/calendario"
                    className="text-xs text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1"
                  >
                    Ver calendario
                    <ChevronRight size={12} />
                  </Link>
                </div>
                
                {calendarEvents.length > 0 ? (
                  <div className="space-y-2">
                    {calendarEvents.slice(0, 4).map((event, i) => (
                      <div 
                        key={event.id || i}
                        className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/40 hover:bg-zinc-800/70 transition-colors"
                      >
                        <div 
                          className="w-1 h-10 rounded-full flex-shrink-0"
                          style={{ backgroundColor: event.color || '#D4AF37' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {event.title}
                          </p>
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Clock size={10} />
                            {event.start && format(new Date(event.start), "d MMM, HH:mm", { locale: es })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <CalendarDays size={32} className="text-zinc-600 mb-2" />
                    <p className="text-sm text-zinc-500">No hay eventos próximos</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Live Indicator */}
          <motion.div 
            variants={itemVariants}
            className="flex items-center justify-center gap-2 text-xs text-zinc-600 pt-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Actualización automática cada minuto
          </motion.div>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default DashboardPage;
